import { create } from 'zustand'
import { PUMPKIN_IDS, SLOT_IDS, type PumpkinId, type SlotId } from './ids'

export type FindDetail = Record<string, number | string | boolean>

export interface Find {
  readonly foundAt: string
  readonly detail?: FindDetail
}

export type FoundMap = Partial<Record<SlotId, Find>>

export interface ServerFind {
  readonly slotId: SlotId
  readonly foundAt: string
  readonly detail?: FindDetail | null
}

interface Persisted {
  readonly found: FoundMap
  readonly spookyOn: boolean
  /** Pumpkins collected so far, in collection order. Local only: the server hears about the full set. */
  readonly pumpkins: readonly PumpkinId[]
  /** The newest admin reset this device has applied. */
  readonly resetAt: string | null
}

interface FindsState extends Persisted {
  readonly email: string | null
  bindEmail: (email: string) => void
  markFound: (slotId: SlotId, detail?: FindDetail) => void
  mergeServer: (rows: readonly ServerFind[]) => void
  setSpooky: (on: boolean) => void
  collectPumpkin: (id: PumpkinId) => void
  /** Clears all local progress if the server reports a reset this device has not seen. Returns whether it did. */
  applyReset: (resetAt: string | null) => boolean
  foundCount: () => number
}

export const storageKeyFor = (email: string): string => `scc:halloween:v1:${email.toLowerCase()}`

function isSlotId(value: string): value is SlotId {
  return (SLOT_IDS as readonly string[]).includes(value)
}

function isPumpkinId(value: unknown): value is PumpkinId {
  return typeof value === 'string' && (PUMPKIN_IDS as readonly string[]).includes(value)
}

function uniquePumpkins(...lists: ReadonlyArray<readonly PumpkinId[] | undefined>): readonly PumpkinId[] {
  return lists.flatMap((l) => l ?? []).filter((id, i, all) => all.indexOf(id) === i)
}

function readPersisted(email: string | null): Persisted {
  const empty: Persisted = { found: {}, spookyOn: false, pumpkins: [], resetAt: null }
  if (!email) return empty
  try {
    const raw = localStorage.getItem(storageKeyFor(email))
    if (!raw) return empty
    const parsed = JSON.parse(raw) as {
      found?: Record<string, Find>
      spookyOn?: boolean
      pumpkins?: readonly string[]
      resetAt?: string | null
    }
    const found = Object.entries(parsed.found ?? {}).reduce<FoundMap>(
      (acc, [id, find]) =>
        isSlotId(id) && typeof find?.foundAt === 'string' ? { ...acc, [id]: find } : acc,
      {},
    )
    const pumpkins = uniquePumpkins((parsed.pumpkins ?? []).filter(isPumpkinId))
    return {
      found,
      spookyOn: parsed.spookyOn === true,
      pumpkins,
      resetAt: typeof parsed.resetAt === 'string' ? parsed.resetAt : null,
    }
  } catch {
    return empty
  }
}

function writePersisted(email: string | null, data: Persisted): void {
  if (!email) return
  try {
    localStorage.setItem(storageKeyFor(email), JSON.stringify(data))
  } catch {
    // storage full or unavailable: the in-memory state still works
  }
}

function earlier(a: Find | undefined, b: Find | undefined): Find | undefined {
  if (!a) return b
  if (!b) return a
  return Date.parse(b.foundAt) < Date.parse(a.foundAt) ? b : a
}

function unionFound(a: FoundMap, b: FoundMap): FoundMap {
  return SLOT_IDS.reduce<FoundMap>((acc, id) => {
    const winner = earlier(a[id], b[id])
    return winner ? { ...acc, [id]: winner } : acc
  }, {})
}

function laterReset(a: string | null, b: string | null): string | null {
  if (!a) return b
  if (!b) return a
  return Date.parse(b) > Date.parse(a) ? b : a
}

export const useFindsStore = create<FindsState>()((set, get) => {
  /** Re-reads storage first so another tab's finds are unioned, never overwritten. */
  const commit = (changes: (current: Persisted) => Persisted): void => {
    const { email, found, spookyOn, pumpkins, resetAt } = get()
    const stored = readPersisted(email)
    // Another tab applied a reset this one has not seen: its copy is the wiped progress, so adopt
    // the stored state instead of unioning it back in. Compared by marker, not by clock.
    const behind = stored.resetAt !== null && laterReset(resetAt, stored.resetAt) !== resetAt
    const base: Persisted = behind
      ? stored
      : {
          found: unionFound(found, stored.found),
          spookyOn,
          pumpkins: uniquePumpkins(pumpkins, stored.pumpkins),
          resetAt: laterReset(resetAt, stored.resetAt),
        }
    const next = changes(base)
    writePersisted(email, next)
    set(next)
  }

  return {
    email: null,
    found: {},
    spookyOn: false,
    pumpkins: [],
    resetAt: null,
    bindEmail: (email) => set({ email, ...readPersisted(email) }),
    markFound: (slotId, detail) =>
      commit((cur) =>
        cur.found[slotId]
          ? cur
          : {
              ...cur,
              found: {
                ...cur.found,
                [slotId]: { foundAt: new Date().toISOString(), ...(detail ? { detail } : {}) },
              },
            },
      ),
    mergeServer: (rows) =>
      commit((cur) => {
        const server = rows.reduce<FoundMap>(
          (acc, row) =>
            isSlotId(row.slotId)
              ? { ...acc, [row.slotId]: { foundAt: row.foundAt, ...(row.detail ? { detail: row.detail } : {}) } }
              : acc,
          {},
        )
        return { ...cur, found: unionFound(cur.found, server) }
      }),
    setSpooky: (on) => commit((cur) => ({ ...cur, spookyOn: on })),
    collectPumpkin: (id) =>
      commit((cur) => (cur.pumpkins.includes(id) ? cur : { ...cur, pumpkins: [...cur.pumpkins, id] })),
    applyReset: (resetAt) => {
      if (!resetAt) return false
      const { email } = get()
      const seen = laterReset(get().resetAt, readPersisted(email).resetAt)
      if (seen && Date.parse(resetAt) <= Date.parse(seen)) return false
      const wiped: Persisted = { found: {}, spookyOn: false, pumpkins: [], resetAt }
      writePersisted(email, wiped)
      set(wiped)
      return true
    },
    foundCount: () => Object.keys(get().found).length,
  }
})
